import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";

import { authApi } from "../services";
import { useAuthStore } from "../stores/zustand";
import { isElectron } from "../util/ElectronDetection";
import { prefetchAllData } from "../hooks/useScheduler";
import onboardingService from "../services/onboardingService";

/**
 * AuthChecker Component
 *
 * Unified authentication checker that handles:
 * - Normal page load validation (using getAuthFlags)
 * - App startup auto-login (using validateToken)
 * - Smart destination routing (onboarding, physio, checkout)
 * - Prefetching all data for both languages
 * - Syncing i18n with user's preferred language
 * - Minimum load time for smooth UX
 */
const AuthChecker = ({ setIsCheckingAuth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const startTimeRef = useRef(Date.now());
  const autoLoginAttempted = useRef(false);
  const prefetchStarted = useRef(false);

  // Get timezone offset
  const getTimezoneOffset = () => {
    const offset = new Date().getTimezoneOffset() / -60;
    return offset >= 0
      ? `+${String(Math.abs(offset)).padStart(2, "0")}`
      : `-${String(Math.abs(offset)).padStart(2, "0")}`;
  };

  // Determines the appropriate destination based on user data
  const determineDestination = useCallback((userData) => {
    const { isPaid, isEmailVerified, current_product, email } = userData;

    // Preventive course users → redirect to external app
    if (current_product === "preventive_course") {
      return "https://vielsitzerkurs.web.app";
    }

    // Physio therapist users
    if (current_product === "physio_therapist") {
      if (isEmailVerified) {
        return "/physio-dashboard";
      } else {
        return "/"; // Unverified physio
      }
    }

    // Physio patient users
    if (current_product === "physio_user") {
      if (isEmailVerified) {
        return "/home/library";
      }
      return "/"; // Unverified physio user
    }

    // Regular users (corporate, preventive_course)
    if (isPaid && isEmailVerified) {
      // Check if user needs onboarding
      const authData = { isPaid, isEmailVerified, current_product, email };
      if (onboardingService.needsOnboarding(authData)) {
        return "/onboarding-exercises";
      }
      return "/home/calendar";
    } else if (!isEmailVerified) {
      return "/login"; // Unverified email
    } else if (isEmailVerified && !isPaid) {
      return "/checkout"; // Verified but not paid
    }
    return "/home/calendar"; // Default fallback
  }, []);

  // Finishes auth check with minimum load time
  const finishAuthCheck = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const minimumLoadTime = 600;

    if (elapsed < minimumLoadTime) {
      setTimeout(() => {
        setIsCheckingAuth(false);
      }, minimumLoadTime - elapsed);
    } else {
      setIsCheckingAuth(false);
    }
  }, [setIsCheckingAuth]);

  // Normal page load validation (uses getAuthFlags)
  const validateAuth = useCallback(async () => {
    const isAuthenticated = useAuthStore.getState().isAuthenticated();

    if (!isAuthenticated) {
      try {
        const flags = await authApi.getAuthFlags();

        // Use setAuthData with flat structure (no auth wrapper)
        useAuthStore.getState().setAuthData({
          name: flags.name || "",
          email: flags.email || "",
          isPaid: flags.isPaid || false,
          isEmailVerified: flags.isEmailVerified ?? false,
          current_product: flags.current_product || null,
          subscribed_products: flags.subscribed_products || [],
          preferredLanguage: flags.preferredLanguage || "de",
          onboarding_completed: flags.onboarding_completed || false,
        });

        //Sync i18n with user's prefferred language
        const userLanguage = flags.preferredLanguage || "de";
        if (i18n.language !== userLanguage) {
          i18n.changeLanguage(userLanguage);
        }
        useAuthStore.getState().setPreferredLanguage(userLanguage);

        // Prefetch Phase-1 (current language) - BLOCKING
        const timezoneOffset = getTimezoneOffset();
        const currentLanguage = flags.preferredLanguage || "de";
        const email = flags.email || "";
        const currentProduct = flags.current_product || null;
        const onboarding_completed = flags.onboarding_completed || false;

        try {
          // AWAIT Phase 1 - don't navigate until data is ready
          await prefetchAllData(
            queryClient,
            timezoneOffset,
            currentLanguage,
            email,
            currentProduct,
            onboarding_completed,
          );
        } catch (error) {
          console.error("Prefetch failed:", error);
          // Navigate anyway even if prefetch fails
        }

        // Determine destination and navigate AFTER Phase 1 completes
        const destination = determineDestination(flags);
        if (destination.startsWith("http")) {
          window.location.href = destination;
        } else if (location.pathname === "/" || location.pathname === "/login") {
          navigate(destination, { replace: true });
        }
      } catch (error) {
        console.error("Failed to fetch auth flags:", error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          useAuthStore.getState().clearAuth();
          // Only show alert and navigate if on authenticated pages
          if (
            location.pathname.includes("/home") ||
            location.pathname.includes("/physio")
          ) {
            alert(t("sessionExpiredMessage"));
            setTimeout(() => {
              navigate("/", { replace: true });
            }, 2000);
          }
        }
      } finally {
        finishAuthCheck();
      }
    } else {
      // Already authenticated - sync i18n with Zustand
      const storedLanguage = useAuthStore.getState().preferredLanguage;
      if (storedLanguage && i18n.language !== storedLanguage) {
        i18n.changeLanguage(storedLanguage);
      }
      finishAuthCheck();
    }
  }, [
    navigate,
    location,
    determineDestination,
    finishAuthCheck,
    i18n,
    queryClient,
    t,
  ]);

  // Prevent double-call on initial mount
  useEffect(() => {
    if (prefetchStarted.current) return;
    prefetchStarted.current = true;

    validateAuth();
  }, [validateAuth]);

  //  App startup auto-login (uses validateToken)

  const attemptAutoLogin = useCallback(async () => {
    if (autoLoginAttempted.current) {
      return;
    }

    autoLoginAttempted.current = true;

    try {
      // Check if already authenticated via authStore
      const isAuthenticated = useAuthStore.getState().isAuthenticated();
      if (!isAuthenticated) {
        return;
      }

      // Validate session with server (different endpoint than getAuthFlags)
      const response = await authApi.validateToken();

      if (response && response.status === "SUCCESS") {
        // Get user data from response
        const userData = response.user || response.data;

        if (userData) {
          // Update authStore with fresh user data using setAuthData (flat structure)
          useAuthStore.getState().setAuthData({
            name: userData.name || "",
            email: userData.email || "",
            isPaid: userData.isPaid || false,
            isEmailVerified: userData.isEmailVerified ?? false,
            current_product: userData.current_product || null,
            subscribed_products: userData.subscribed_products || [],
            preferredLanguage: userData.preferredLanguage || "de",
            onboarding_completed: userData.onboarding_completed || false,
            first_login: userData.first_login || false,
          });

          // SYNC i18n WITH USER'S PREFERRED LANGUAGE
          const userLanguage = userData.preferredLanguage || "de";
          if (i18n.language !== userLanguage) {
            i18n.changeLanguage(userLanguage);
          }
          useAuthStore.getState().setPreferredLanguage(userLanguage);

          // PREFETCH PHASE 1 (current language) - BLOCKING
          const timezoneOffset = getTimezoneOffset();
          const currentLanguage = userData.preferredLanguage || "de";
          const email = userData.email || "";
          const currentProduct = userData.current_product || null;
          const onboarding_completed = userData.onboarding_completed || false;

          try {
            // AWAIT Phase 1 - don't navigate until data is ready
            await prefetchAllData(
              queryClient,
              timezoneOffset,
              currentLanguage,
              email,
              currentProduct,
              onboarding_completed,
            );
          } catch (error) {
            console.error("Prefetch failed:", error);
            // Navigate anyway even if prefetch fails
          }

          // Navigate to appropriate screen AFTER Phase 1 completes
          const destination = determineDestination(userData);

          // External app redirect (e.g. preventive_course)
          if (destination.startsWith("http")) {
            window.location.href = destination;
            return;
          }

          // Use Electron navigation API if available
          if (isElectron() && window.electronAPI?.navigation) {
            try {
              await window.electronAPI.navigation.navigateToAuthenticatedState(
                true,
              );
            } catch (navError) {
              console.warn(
                "Electron navigation failed, falling back to React Router:",
                navError,
              );
              navigate(destination, {
                replace: true,
                state: {
                  autoLogin: true,
                  wasStartup: true,
                  email: userData.email,
                },
              });
            }
          } else {
            navigate(destination, {
              replace: true,
              state: {
                autoLogin: true,
                wasStartup: true,
                email: userData.email,
              },
            });
          }
        }
      } else {
        console.log("Session validation failed:", response);
      }
    } catch (error) {
      console.error("Auto-login failed:", error);

      // If session is expired or invalid, clear auth
      if (error.response?.status === 401 || error.response?.status === 403) {
        useAuthStore.getState().clearAuth();

        // Only show alert and navigate if on authenticated pages
        if (
          location.pathname.includes("/home") ||
          location.pathname.includes("/physio")
        ) {
          alert(t("sessionExpiredMessage"));
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 2000);
        }
      }
    }
  }, [navigate, determineDestination, i18n, queryClient, t, location]);

  /**
   * Listen for Electron startup events
   */
  useEffect(() => {
    if (!isElectron() || !window.electronAPI?.startup) {
      return;
    }

    const cleanup = window.electronAPI.startup.onLaunchedAtStartup(
      async (startupData) => {
        // Add delay to ensure services are initialized
        setTimeout(async () => {
          await attemptAutoLogin();
        }, 1000);
      },
    );

    return cleanup;
  }, [attemptAutoLogin]);

  return null;
};

export default AuthChecker;
